import { useCallback, useMemo, useState } from "react";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import {
  companySlugDriftService,
  type ApplyResponse,
  type DriftCategory,
  type DriftRow,
} from "../../../api/companySlugDrift.service.js";
import { buildPlannedRollbackCsv, buildRollbackCsv, rollbackFileName } from "./rollback.js";
import { parseRollbackCsv, type ParsedRollbackRow } from "./parseRollback.js";
import styles from "./CompanySlugDrift.module.css";

/**
 * SOFTSUPP-532 — repairs offices whose denormalised company identity drifted from the
 * parent company. Renaming a firm used to leave `offices.company_slug` / `company_name`
 * and the office slug's company prefix behind, which splits the firm's pages across two
 * slugs and 404s every tab in its menu.
 *
 * Two-phase by design, mirroring the Profile Website Change tool: the scan writes nothing
 * and is always safe to run; nothing is written until an operator reviews the proposals
 * and confirms.
 */

const CATEGORY_LABELS: Record<DriftCategory, string> = {
  company_slug: "Company slug (denormalised copy)",
  company_name: "Company name (denormalised copy)",
  office_slug_prefix: "Office slug prefix — CHANGES THE OFFICE URL",
};

const CATEGORY_ORDER: DriftCategory[] = ["company_slug", "company_name", "office_slug_prefix"];

/** The only category whose repair changes a live address. */
const RISKY: DriftCategory = "office_slug_prefix";

const rowKey = (row: DriftRow) => `${row.officeId}:${row.category}`;

function CompanySlugDriftPage() {
  const [rows, setRows] = useState<DriftRow[]>([]);
  const [summary, setSummary] = useState<Record<DriftCategory, number> | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [activeCategory, setActiveCategory] = useState<DriftCategory | "all">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isScanning, setIsScanning] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<ApplyResponse | null>(null);
  const [appliedCover, setAppliedCover] = useState<DriftRow[]>([]);
  const [restoreRows, setRestoreRows] = useState<ParsedRollbackRow[]>([]);
  const [restoreErrors, setRestoreErrors] = useState<string[]>([]);
  const [restoreFileName, setRestoreFileName] = useState<string | null>(null);
  const [isConfirmingRestore, setIsConfirmingRestore] = useState(false);

  // `preserveApplied` keeps the last run's summary — and with it the authoritative
  // rollback download — on screen. The re-scan that follows an apply MUST pass it:
  // clearing the banner there takes the pre-write values off the screen the moment they
  // exist.
  const scan = useCallback(async (options?: { preserveApplied?: boolean }) => {
    setIsScanning(true);
    setError(null);
    if (!options?.preserveApplied) {
      setApplied(null);
      setAppliedCover([]);
    }
    try {
      const result = await companySlugDriftService.scan();
      setRows(result.rows);
      setSummary(result.summary);
      setTruncated(result.truncated);
      // Nothing is pre-selected: every write here is an explicit operator choice.
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsScanning(false);
    }
  }, []);

  const visibleRows = useMemo(
    () => (activeCategory === "all" ? rows : rows.filter(r => r.category === activeCategory)),
    [rows, activeCategory],
  );

  const selectedRows = useMemo(() => rows.filter(r => selected.has(rowKey(r))), [rows, selected]);

  const selectedRiskyCount = useMemo(
    () => selectedRows.filter(r => r.category === RISKY).length,
    [selectedRows],
  );

  const toggleRow = (row: DriftRow) => {
    setSelected(prev => {
      const next = new Set(prev);
      const key = rowKey(row);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleVisible = () => {
    const allSelected = visibleRows.every(r => selected.has(rowKey(r)));
    setSelected(prev => {
      const next = new Set(prev);
      for (const row of visibleRows) {
        if (allSelected) next.delete(rowKey(row));
        else next.add(rowKey(row));
      }
      return next;
    });
  };

  const applySelected = async () => {
    setIsApplying(true);
    setError(null);
    // Snapshot the rows this run covers BEFORE the re-scan below replaces them — the
    // rollback file needs their company names to stay readable.
    const covered = selectedRows;
    try {
      const result = await companySlugDriftService.apply(
        covered.map(r => ({
          officeId: r.officeId,
          category: r.category,
          proposed: r.proposed,
        })),
      );
      setApplied(result);
      setAppliedCover(covered);
      // The scan is now stale by construction — re-run it rather than showing rows that
      // may already have been repaired, but keep the run summary so its rollback file
      // stays reachable.
      await scan({ preserveApplied: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsApplying(false);
      setIsConfirming(false);
    }
  };

  const onRollbackFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setApplied(null);
    const text = await file.text();
    const { rows: parsed, errors } = parseRollbackCsv(text);
    setRestoreRows(parsed);
    setRestoreErrors(errors);
    setRestoreFileName(file.name);
  };

  const runRestore = async () => {
    setIsApplying(true);
    setError(null);
    try {
      // "restore" writes the file's values verbatim. "repair" would refuse every row here:
      // a rollback value no longer matches what the company implies, which is the point.
      const result = await companySlugDriftService.apply(
        restoreRows.map(r => ({
          officeId: r.officeId,
          category: r.category,
          proposed: r.proposed,
        })),
        "restore",
      );
      setApplied(result);
      setAppliedCover([]);
      setRestoreRows([]);
      setRestoreErrors([]);
      setRestoreFileName(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsApplying(false);
      setIsConfirmingRestore(false);
    }
  };

  const downloadCsv = (csv: string) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = rollbackFileName(new Date());
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadPlannedRollback = () => {
    const { csv, rowCount } = buildPlannedRollbackCsv(selectedRows);
    // A header-only file looks like a rollback but undoes nothing. Say so rather than
    // letting it land in Downloads and be trusted.
    if (rowCount === 0) {
      setError("Nothing to roll back — select the rows you intend to apply first.");
      return;
    }
    downloadCsv(csv);
  };

  const downloadRollback = () => {
    if (!applied) return;
    const { csv, rowCount } = buildRollbackCsv(applied.results, appliedCover);
    if (rowCount === 0) {
      setError("That run wrote nothing, so there is nothing to roll back.");
      return;
    }
    downloadCsv(csv);
  };

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Company Slug Drift"
        subtitle="Repair offices whose company name/slug copy drifted from the parent company"
      />

      <div className={styles.toolbar}>
        <button type="button" onClick={() => scan()} disabled={isScanning || isApplying}>
          {isScanning ? "Scanning…" : "Run scan"}
        </button>
        <button
          type="button"
          onClick={() => setIsConfirming(true)}
          disabled={selectedRows.length === 0 || isScanning || isApplying}
        >
          {isApplying ? "Applying…" : `Apply ${selectedRows.length} selected`}
        </button>
        {/* Available BEFORE the write, so an undo exists even if the tab dies mid-run. */}
        <button
          type="button"
          onClick={downloadPlannedRollback}
          disabled={selectedRows.length === 0 || isApplying}
        >
          Download rollback first
        </button>
      </div>

      <div className={styles.restore}>
        <label htmlFor="rollback-file">
          <strong>Restore from a rollback file</strong> — puts offices back to the values a previous
          run overwrote. Values are written verbatim, so this is the only way to undo a run.
        </label>
        <input
          id="rollback-file"
          type="file"
          accept=".csv,text/csv"
          onChange={e => onRollbackFile(e.target.files?.[0])}
          disabled={isScanning || isApplying}
        />
        {restoreFileName && (
          <div>
            {restoreFileName}: {restoreRows.length} row(s) ready
            {restoreErrors.length > 0 && `, ${restoreErrors.length} rejected`}
          </div>
        )}
        {restoreErrors.length > 0 && (
          <ul className={styles.error}>
            {restoreErrors.slice(0, 10).map(e => (
              <li key={e}>{e}</li>
            ))}
            {restoreErrors.length > 10 && <li>…and {restoreErrors.length - 10} more</li>}
          </ul>
        )}
        {restoreRows.length > 0 && (
          <button type="button" onClick={() => setIsConfirmingRestore(true)} disabled={isApplying}>
            Restore {restoreRows.length} row(s)
          </button>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {truncated && (
        <div className={styles.notice}>
          The scan hit its row cap — this is <strong>not</strong> the full list. Repair what is
          shown and run the scan again.
        </div>
      )}

      {summary && (
        <div className={styles.summary}>
          <button
            type="button"
            className={activeCategory === "all" ? styles.chipActive : styles.chip}
            onClick={() => setActiveCategory("all")}
          >
            All ({rows.length})
          </button>
          {CATEGORY_ORDER.map(category => {
            const isActive = activeCategory === category;
            const risky = category === RISKY;
            const className = risky
              ? isActive
                ? styles.chipRiskyActive
                : styles.chipRisky
              : isActive
                ? styles.chipActive
                : styles.chip;
            return (
              <button
                key={category}
                type="button"
                className={className}
                onClick={() => setActiveCategory(category)}
              >
                {CATEGORY_LABELS[category]} ({summary[category]})
              </button>
            );
          })}
        </div>
      )}

      {applied && (
        <div className={styles.notice}>
          Applied {applied.summary.applied} · skipped as already correct {applied.summary.noop} ·
          refused as superseded {applied.summary.stale} · missing {applied.summary.not_found} ·
          failed {applied.summary.failed}
          {applied.summary.applied > 0 && (
            <>
              {" — "}
              <button type="button" onClick={downloadRollback}>
                Download rollback file
              </button>
              {
                " Keep it: this run overwrote office identity in place and the API keeps no history."
              }
            </>
          )}
        </div>
      )}

      {rows.length === 0 && !isScanning && (
        <div className={styles.empty}>
          {summary ? "No drift found — every office matches its company." : "Run a scan to begin."}
        </div>
      )}

      {visibleRows.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label="Select all visible rows"
                  checked={visibleRows.every(r => selected.has(rowKey(r)))}
                  onChange={toggleVisible}
                />
              </th>
              <th>Company</th>
              <th>Category</th>
              <th>Current → proposed</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(row => (
              <tr key={rowKey(row)}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Select ${row.companyName} ${row.category}`}
                    checked={selected.has(rowKey(row))}
                    onChange={() => toggleRow(row)}
                  />
                </td>
                <td>{row.companyName}</td>
                <td>{CATEGORY_LABELS[row.category]}</td>
                <td>
                  <div className={styles.from}>{row.current ?? "(empty)"}</div>
                  <div className={styles.to}>{row.proposed}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isConfirmingRestore && (
        <ConfirmationModal
          title="Restore previous values"
          message={`${restoreRows.length} office row(s) will be written back to the values in ${restoreFileName}.`}
          warningMessage={
            restoreRows.some(r => r.category === "office_slug_prefix")
              ? "Some rows restore an office slug, which changes those office page URLs back. Links published since the original run will stop resolving."
              : "These rows only restore denormalised copies — no page URL changes."
          }
          confirmButtonText="Restore"
          confirmButtonColor="error"
          onClose={() => setIsConfirmingRestore(false)}
          onConfirm={runRestore}
        />
      )}

      {isConfirming && (
        <ConfirmationModal
          title="Apply company identity repairs"
          message={`${selectedRows.length} row(s) will be written to the address book.`}
          warningMessage={
            selectedRiskyCount > 0
              ? `${selectedRiskyCount} of them rewrite an office slug, which CHANGES that office page's URL. Links to the old address will stop resolving.`
              : "These rows only repair denormalised copies — no page URL changes."
          }
          confirmButtonText="Apply"
          confirmButtonColor={selectedRiskyCount > 0 ? "error" : "default"}
          onClose={() => setIsConfirming(false)}
          onConfirm={applySelected}
        />
      )}
    </div>
  );
}

export default CompanySlugDriftPage;
